import { useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react"
import { change as pctChange } from "./format"

/**
 * Dependency-free charts in the style of Shopify Analytics. Current period in
 * blue, comparison period dashed light blue; every chart has a hover tooltip.
 * Rendered in real pixels (measured container width) so text stays readable
 * at any size.
 */
export const COLORS = {
  current: "var(--k-series-1)", // Keel hull: chart series 1
  previous: "var(--k-series-2)", // Keel tide: series 2 (drawn dashed)
  grid: "var(--k-chart-grid)",
  axis: "var(--k-chart-axis)",
  target: "var(--k-chart-axis)",
  annotation: "var(--k-blue)",
  // Keel: hull, tide first; then distinct hues (never brass: gold is not a data colour).
  series: ["var(--k-series-1)", "var(--k-series-2)", "var(--k-series-3)", "var(--k-chart-4)", "var(--k-chart-5)"],
}

function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [w, setW] = useState(0)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    setW(el.clientWidth)
    const ro = new ResizeObserver(([e]) => setW(Math.round(e.contentRect.width)))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  return [ref, w] as const
}

function niceMax(max: number) {
  if (max <= 0) return 1
  const pow = 10 ** Math.floor(Math.log10(max))
  const step = [1, 2, 2.5, 5, 10].map((m) => m * pow).find((s) => max / s <= 4) ?? pow * 10
  return Math.ceil(max / step) * step
}
function niceRange(min: number, max: number) {
  if (min >= 0) return { lo: 0, hi: niceMax(max) }
  const m = niceMax(Math.max(Math.abs(min), Math.abs(max)))
  return { lo: max <= 0 ? -m : -niceMax(Math.abs(min)), hi: max <= 0 ? 0 : niceMax(max) }
}

/** Shopify-style change indicator: ↗ 12% / ↘ 4%, green when the move is good. */
export function Change({ cur, prev, good = "up", className = "" }: { cur: number | null | undefined; prev: number | null | undefined; good?: "up" | "down"; className?: string }) {
  const c = pctChange(cur, prev)
  if (c === null) return <span className={`text-[12px] text-ui-fg-muted ${className}`}>—</span>
  if (c === 0) return <span className={`text-[12px] text-ui-fg-muted ${className}`}>0%</span>
  const up = c > 0
  const positive = good === "up" ? up : !up
  return (
    <span className={`inline-flex items-center gap-0.5 text-[12px] font-medium ${positive ? "text-[var(--k-positive)]" : "text-[var(--k-alert)]"} ${className}`} title={`${up ? "Up" : "Down"} ${Math.abs(c)}% from the comparison period`}>
      <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" className={up ? "" : "rotate-90"}>
        <path d="M2 8 8 2M4 2h4v4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {Math.abs(c).toLocaleString("en-US", { maximumFractionDigits: 1 })}%
    </span>
  )
}

export type TimePoint = { key: string; label: string; long: string; value: number | null; prev?: number | null; prevLong?: string | null; stack?: Record<string, number> }

/**
 * Line / area / bar / stacked-bar chart over time with the comparison period,
 * an optional target line and annotation markers.
 */
export function TimeChart({
  points,
  format,
  axisFormat,
  kind = "line",
  height = 220,
  currentLabel,
  previousLabel,
  target,
  annotations = [],
  stackKeys,
  stackLabel = (k: string) => k,
  gaps = false,
}: {
  points: TimePoint[]
  format: (v: number) => string
  /** Short labels for the y axis (defaults to format). */
  axisFormat?: (v: number) => string
  kind?: "line" | "area" | "bar" | "stacked_bar"
  height?: number
  currentLabel?: string
  previousLabel?: string | null
  target?: number | null
  annotations?: { key: string; note: string }[]
  stackKeys?: string[]
  stackLabel?: (k: string) => string
  /** Lines stop at missing values (e.g. hours still to come today) instead of drawing them as 0. */
  gaps?: boolean
}) {
  const [ref, W] = useWidth<HTMLDivElement>()
  const [hoverRaw, setHover] = useState<number | null>(null)
  // Data can change under the mouse (new range): never point past the end.
  const hover = hoverRaw != null && hoverRaw < points.length ? hoverRaw : null
  const H = height
  const pad = { l: 46, r: 10, t: 12, b: 26 }
  const hasPrev = points.some((p) => p.prev != null) && previousLabel !== null
  const stacked = kind === "stacked_bar" && stackKeys?.length
  const values = points.flatMap((p) => (stacked ? [Object.values(p.stack ?? {}).reduce((a, b) => a + Math.max(b, 0), 0)] : [p.value ?? 0, hasPrev ? (p.prev ?? 0) : 0]))
  const { lo, hi } = niceRange(Math.min(0, ...values, target ?? 0), Math.max(...values, target ?? 0, 0))
  const n = points.length
  const iw = Math.max(W - pad.l - pad.r, 10)
  const bar = kind === "bar" || stacked
  const x = (i: number) => (bar ? pad.l + (i + 0.5) * (iw / Math.max(n, 1)) : pad.l + (n <= 1 ? iw / 2 : (i / (n - 1)) * iw))
  const y = (v: number) => pad.t + (1 - (v - lo) / (hi - lo || 1)) * (H - pad.t - pad.b)
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => lo + t * (hi - lo))
  const every = Math.max(1, Math.ceil(n / Math.max(2, Math.floor(iw / 70))))
  const line = (get: (p: TimePoint) => number | null | undefined) =>
    gaps
      ? points.map((p, i) => (get(p) == null ? "" : `${i && get(points[i - 1]) != null ? "L" : "M"}${x(i).toFixed(1)},${y(get(p)!).toFixed(1)}`)).join(" ")
      : points.map((p, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(get(p) ?? 0).toFixed(1)}`).join(" ")
  const bw = Math.max(2, Math.min(28, (iw / Math.max(n, 1)) * (hasPrev && !stacked ? 0.36 : 0.62)))
  const ann = new Map(annotations.map((a) => [a.key, a.note]))

  return (
    <div ref={ref} className="relative w-full select-none" style={{ height: H }} onMouseLeave={() => setHover(null)}>
      {W > 0 && (
        <svg width={W} height={H} role="img" aria-label={`${currentLabel ?? "Chart"} over time`}>
          {ticks.map((t, i) => (
            <g key={i}>
              <line x1={pad.l} x2={W - pad.r} y1={y(t)} y2={y(t)} stroke={COLORS.grid} strokeDasharray={t === 0 ? undefined : "2 3"} />
              <text x={pad.l - 6} y={y(t) + 3.5} textAnchor="end" fontSize="11" fill={COLORS.axis}>{(axisFormat ?? format)(t)}</text>
            </g>
          ))}
          {points.map((p, i) =>
            i % every === 0 ? (
              <text key={i} x={x(i)} y={H - 8} textAnchor={!bar && i === 0 ? "start" : "middle"} fontSize="11" fill={COLORS.axis}>{p.label}</text>
            ) : null
          )}
          {target != null && target > 0 && (
            <g>
              <line x1={pad.l} x2={W - pad.r} y1={y(target)} y2={y(target)} stroke={COLORS.target} strokeDasharray="6 4" strokeWidth="1.5" />
              <text x={W - pad.r} y={y(target) - 4} textAnchor="end" fontSize="11" fill={COLORS.target}>Target {(axisFormat ?? format)(target)}</text>
            </g>
          )}
          {bar &&
            points.map((p, i) => {
              if (stacked) {
                let acc = 0
                return (
                  <g key={i}>
                    {stackKeys!.map((k, si) => {
                      const v = Math.max(p.stack?.[k] ?? 0, 0)
                      if (!v) return null
                      const top = y(acc + v)
                      const h = Math.max(y(acc) - top - (acc > 0 ? 2 : 0), 1)
                      acc += v
                      return <rect key={k} x={x(i) - bw / 2} y={top} width={bw} height={h} rx={2} fill={COLORS.series[si % COLORS.series.length]} />
                    })}
                  </g>
                )
              }
              const v = p.value ?? 0
              const pv = p.prev ?? 0
              return (
                <g key={i}>
                  {hasPrev && <rect x={x(i) - bw - 1} y={Math.min(y(pv), y(0))} width={bw} height={Math.max(Math.abs(y(0) - y(pv)), pv ? 1 : 0)} rx={2} fill={COLORS.previous} />}
                  <rect x={hasPrev ? x(i) + 1 : x(i) - bw / 2} y={Math.min(y(v), y(0))} width={bw} height={Math.max(Math.abs(y(0) - y(v)), v ? 1 : 0)} rx={2} fill={COLORS.current} />
                </g>
              )
            })}
          {!bar && hasPrev && <path d={line((p) => p.prev)} fill="none" stroke={COLORS.previous} strokeWidth="2" strokeDasharray="4 4" />}
          {!bar && kind === "area" && <path d={`${line((p) => p.value)} L${x(n - 1)},${y(0)} L${x(0)},${y(0)} Z`} fill={COLORS.current} opacity="0.12" />}
          {!bar && <path d={line((p) => p.value)} fill="none" stroke={COLORS.current} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />}
          {!bar && n === 1 && <circle cx={x(0)} cy={y(points[0].value ?? 0)} r="4" fill={COLORS.current} />}
          {!bar && gaps && (() => {
            // Where the line stops ("now" on a day still in progress).
            let last = -1
            points.forEach((p, i) => p.value != null && (last = i))
            return last >= 0 && last < n - 1 ? <circle cx={x(last)} cy={y(points[last].value!)} r="3.5" fill={COLORS.current} stroke="var(--k-chart-halo)" strokeWidth="2" /> : null
          })()}
          {points.map((p, i) =>
            ann.has(p.key) ? (
              <g key={`a${i}`}>
                <line x1={x(i)} x2={x(i)} y1={pad.t} y2={H - pad.b} stroke={COLORS.annotation} strokeDasharray="2 3" opacity="0.6" />
                <circle cx={x(i)} cy={H - pad.b} r="4" fill={COLORS.annotation} stroke="var(--k-chart-halo)" strokeWidth="2" />
              </g>
            ) : null
          )}
          {points.map((_, i) => (
            <rect key={`h${i}`} x={x(i) - iw / Math.max(n, 1) / 2} y={0} width={iw / Math.max(n, 1)} height={H} fill="transparent" onMouseEnter={() => setHover(i)} onTouchStart={() => setHover(i)} />
          ))}
          {hover != null && !bar && (
            <g pointerEvents="none">
              <line x1={x(hover)} x2={x(hover)} y1={pad.t} y2={H - pad.b} stroke="var(--k-chart-cursor)" />
              {hasPrev && <circle cx={x(hover)} cy={y(points[hover].prev ?? 0)} r="4" fill="var(--k-chart-halo)" stroke={COLORS.previous} strokeWidth="2" />}
              {!(gaps && points[hover].value == null) && <circle cx={x(hover)} cy={y(points[hover].value ?? 0)} r="4" fill={COLORS.current} stroke="var(--k-chart-halo)" strokeWidth="2" />}
            </g>
          )}
        </svg>
      )}
      {hover != null && points[hover] && (
        <Tooltip x={x(hover)} W={W}>
          {stacked ? (
            <>
              <div className="mb-1 font-medium">{points[hover].long}</div>
              {stackKeys!.map((k, si) => (
                <Line key={k} color={COLORS.series[si % COLORS.series.length]} label={stackLabel(k)} value={format(points[hover].stack?.[k] ?? 0)} />
              ))}
            </>
          ) : (
            <>
              <Line color={COLORS.current} label={points[hover].long} value={points[hover].value == null ? "—" : format(points[hover].value!)} />
              {hasPrev && <Line color={COLORS.previous} dashed label={points[hover].prevLong ?? previousLabel ?? "Previous"} value={points[hover].prev == null ? "—" : format(points[hover].prev!)} />}
              {hasPrev && (
                <div className="mt-1">
                  <Change cur={points[hover].value} prev={points[hover].prev} />
                </div>
              )}
              {ann.get(points[hover].key) && <div className="mt-1 max-w-[220px] border-t pt-1 text-ui-fg-subtle">📌 {ann.get(points[hover].key)}</div>}
            </>
          )}
        </Tooltip>
      )}
    </div>
  )
}

function Line({ color, label, value, dashed }: { color: string; label: string; value: string; dashed?: boolean }) {
  return (
    <div className="flex items-center gap-2 whitespace-nowrap">
      <span className="inline-block h-0 w-3" style={{ borderTop: `2px ${dashed ? "dashed" : "solid"} ${color}` }} />
      <span className="text-ui-fg-subtle">{label}</span>
      <span className="ml-auto pl-3 font-medium text-ui-fg-base">{value}</span>
    </div>
  )
}

function Tooltip({ x, W, children }: { x: number; W: number; children: ReactNode }) {
  const left = x > W * 0.6
  return (
    <div
      role="tooltip"
      className="pointer-events-none absolute top-1 z-10 min-w-[160px] rounded-lg border border-[color:var(--k-fill-strong)] bg-ui-bg-base px-3 py-2 text-[12px] shadow-[0_4px_12px_rgba(0,0,0,0.12)] dark:shadow-[0_4px_12px_rgba(0,0,0,0.5)]"
      style={left ? { right: W - x + 12 } : { left: x + 12 }}
    >
      {children}
    </div>
  )
}

/** Legend under time charts: current period solid, comparison dashed. */
export function PeriodLegend({ current, previous }: { current: string; previous?: string | null }) {
  return (
    <div className="mt-2 flex flex-wrap justify-center gap-x-4 gap-y-1 text-[12px] text-ui-fg-subtle">
      <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-full" style={{ background: COLORS.current }} />{current}</span>
      {previous && <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-full" style={{ background: COLORS.previous }} />{previous}</span>}
    </div>
  )
}

export type BarItem = { key: string; label: string; value: number; prev?: number | null; sub?: string }

/** Shopify's horizontal bar list ("Sessions by location"): bar per item, comparison bar under it. */
export function HBarList({ items, format, good = "up", showPrev = true }: { items: BarItem[]; format: (v: number) => string; good?: "up" | "down"; showPrev?: boolean }) {
  const max = Math.max(1, ...items.map((i) => Math.max(Math.abs(i.value), Math.abs(i.prev ?? 0))))
  return (
    <ul className="flex flex-col gap-3">
      {items.map((it) => (
        <li key={it.key} className="text-[12px]">
          <div className="mb-1 truncate text-ui-fg-subtle" title={it.label}>{it.label}</div>
          <div className="flex items-center gap-2">
            <div className="h-3 min-w-[2px] rounded-[3px]" style={{ width: `${(Math.abs(it.value) / max) * 75}%`, background: COLORS.current }} />
            <span className="whitespace-nowrap font-medium text-ui-fg-base">{format(it.value)}</span>
            {showPrev && it.prev !== undefined && <Change cur={it.value} prev={it.prev} good={good} />}
          </div>
          {showPrev && it.prev != null && (
            <div className="mt-1 flex items-center gap-2">
              <div className="h-3 min-w-[2px] rounded-[3px]" style={{ width: `${(Math.abs(it.prev) / max) * 75}%`, background: COLORS.previous }} />
              <span className="whitespace-nowrap text-ui-fg-muted">{format(it.prev)}</span>
            </div>
          )}
        </li>
      ))}
    </ul>
  )
}

/** Donut with the total in the middle and a legend with values and changes (Sessions by device type). */
export function Donut({ items, format, total, prevTotal, good = "up" }: { items: BarItem[]; format: (v: number) => string; total: number; prevTotal?: number | null; good?: "up" | "down" }) {
  const [hover, setHover] = useState<number | null>(null)
  const size = 168
  const r = 72
  const w = 22
  const sum = items.reduce((s, i) => s + Math.max(i.value, 0), 0) || 1
  let a0 = -Math.PI / 2
  const arcs = items.map((it, i) => {
    const a1 = a0 + (Math.max(it.value, 0) / sum) * Math.PI * 2
    const gap = items.length > 1 ? 0.025 : 0
    const s = a0 + gap
    const e = Math.max(a1 - gap, s)
    const large = e - s > Math.PI ? 1 : 0
    const p = (a: number, rr: number) => `${size / 2 + rr * Math.cos(a)},${size / 2 + rr * Math.sin(a)}`
    const d = `M${p(s, r)} A${r},${r} 0 ${large} 1 ${p(e, r)} L${p(e, r - w)} A${r - w},${r - w} 0 ${large} 0 ${p(s, r - w)} Z`
    a0 = a1
    return { d, color: COLORS.series[i % COLORS.series.length], it }
  })
  return (
    <div className="flex flex-wrap items-center gap-4">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} role="img" aria-label="Breakdown">
          {items.length === 0 || sum === 0 ? <circle cx={size / 2} cy={size / 2} r={r - w / 2} fill="none" stroke={COLORS.grid} strokeWidth={w} /> : null}
          {arcs.map((a, i) => (
            <path key={a.it.key} d={a.d} fill={a.color} opacity={hover == null || hover === i ? 1 : 0.35} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} />
          ))}
        </svg>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="max-w-[104px] truncate text-[15px] font-semibold text-ui-fg-base">{hover == null ? format(total) : format(items[hover].value)}</span>
          {hover == null ? prevTotal !== undefined && <Change cur={total} prev={prevTotal} good={good} /> : <span className="text-[11px] text-ui-fg-subtle">{items[hover].label}</span>}
        </div>
      </div>
      <ul className="flex min-w-[200px] flex-1 flex-col gap-2 text-[12px]">
        {items.map((it, i) => (
          <li key={it.key} className="flex items-center gap-2" onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
            <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-[3px]" style={{ background: COLORS.series[i % COLORS.series.length] }} />
            <span className="truncate text-ui-fg-subtle" title={it.label}>{it.label}</span>
            <span className="ml-auto font-medium text-ui-fg-base">{format(it.value)}</span>
            {it.prev !== undefined && <Change cur={it.value} prev={it.prev} good={good} className="w-14 justify-end text-right" />}
          </li>
        ))}
      </ul>
    </div>
  )
}

export type FunnelStep = { label: string; value: number; prev?: number | null }

/** Conversion rate breakdown: Sessions → Added to cart → Reached checkout → Completed. */
export function Funnel({ steps, height = 170 }: { steps: FunnelStep[]; height?: number }) {
  const [ref, W] = useWidth<HTMLDivElement>()
  const top = steps[0]?.value || 0
  const topPrev = steps[0]?.prev || 0
  const colW = W / Math.max(steps.length, 1)
  const H = height
  const h = (v: number) => (top ? Math.max((v / top) * H, v ? 3 : 0) : 0)
  return (
    <div ref={ref} className="w-full">
      <div className="grid text-[12px]" style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}>
        {steps.map((s, i) => {
          const pct = top ? Math.round((s.value / top) * 10000) / 100 : 0
          const pp = topPrev && s.prev != null ? Math.round((s.prev / topPrev) * 10000) / 100 : null
          return (
            <div key={s.label} className={`px-2 pb-2 ${i ? "border-l border-[color:var(--k-rule)]" : ""}`}>
              <div className="truncate text-ui-fg-subtle" title={s.label}>{s.label}</div>
              <div className="text-[14px] font-semibold text-ui-fg-base">{pct.toLocaleString("en-US", { maximumFractionDigits: 2 })}%</div>
              <div className="text-ui-fg-subtle">{s.value.toLocaleString("en-US")}</div>
              {s.prev !== undefined && <Change cur={pct} prev={pp} />}
            </div>
          )
        })}
      </div>
      {W > 0 && (
        <svg width={W} height={H} role="img" aria-label="Conversion funnel">
          {steps.map((s, i) => {
            const x0 = i * colW + 4
            const bw = colW * 0.55
            const yv = H - h(s.value)
            const next = steps[i + 1]
            return (
              <g key={s.label}>
                <rect x={x0} y={yv} width={bw} height={H - yv} rx={3} fill={COLORS.current} />
                {next && <path d={`M${x0 + bw},${yv} L${x0 + colW},${H - h(next.value)} L${x0 + colW},${H} L${x0 + bw},${H} Z`} fill={COLORS.current} opacity="0.15" />}
              </g>
            )
          })}
        </svg>
      )}
    </div>
  )
}

/** Small trend line for KPI tiles. */
export function Sparkline({ values, prev, width = 64, height = 22 }: { values: number[]; prev?: (number | null)[]; width?: number; height?: number }) {
  const all = [...values, ...(prev ?? []).map((v) => v ?? 0)]
  const max = Math.max(1, ...all)
  const min = Math.min(0, ...all)
  const x = (i: number) => (values.length <= 1 ? width / 2 : (i / (values.length - 1)) * width)
  const y = (v: number) => height - 2 - ((v - min) / (max - min || 1)) * (height - 4)
  const path = (vs: (number | null)[]) => vs.map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(v ?? 0).toFixed(1)}`).join(" ")
  return (
    <svg width={width} height={height} aria-hidden="true">
      {prev && prev.length > 0 && <path d={path(prev)} fill="none" stroke={COLORS.previous} strokeWidth="1.5" strokeDasharray="3 3" />}
      <path d={path(values)} fill="none" stroke={COLORS.current} strokeWidth="1.5" />
    </svg>
  )
}

/** Cohort heatmap: darker blue = higher value. Empty cells (future periods) stay blank. */
export function Heatmap({ rows, columns, format, rowLabel, rowSub }: { rows: { key: string; values: (number | null)[] }[]; columns: string[]; format: (v: number) => string; rowLabel: (k: string) => string; rowSub?: (k: string) => ReactNode }) {
  const max = useMemo(() => Math.max(1, ...rows.flatMap((r) => r.values.filter((v): v is number => v != null))), [rows])
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-separate border-spacing-0.5 text-[12px]">
        <thead>
          <tr>
            <th className="!bg-transparent px-2 py-1 text-left font-medium text-ui-fg-subtle">Cohort</th>
            {columns.map((c) => (
              <th key={c} className="!bg-transparent px-2 py-1 text-center font-medium text-ui-fg-subtle">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <td className="whitespace-nowrap px-2 py-1.5 text-ui-fg-subtle">
                {rowLabel(r.key)}
                {rowSub && <div className="text-[11px] text-ui-fg-muted">{rowSub(r.key)}</div>}
              </td>
              {columns.map((_, i) => {
                const v = r.values[i]
                const t = v == null ? 0 : Math.min(1, v / max)
                return (
                  <td
                    key={i}
                    className="min-w-[56px] rounded-[4px] px-2 py-1.5 text-center"
                    style={v == null ? undefined : { background: `rgba(var(--k-heat-rgb),${0.08 + t * 0.72})`, color: t > 0.55 ? "var(--k-heat-ink-strong)" : "var(--k-heat-ink)" }}
                    title={v == null ? "Not reached yet" : format(v)}
                  >
                    {v == null ? "" : format(v)}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
